import {
  isTreatmentPlanSectionOptional,
  TreatmentPlan,
  TreatmentPlanFormType,
} from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { useStoreFormState } from "@/modules/patients/detail/progress-note/hooks/useStoreFormState"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"

/**
 * Normalizes text to match the format produced by the rich text editor.
 * The editor removes trailing/leading spaces around newlines, so we need to
 * normalize the backend data to prevent false dirty state detection.
 */
const normalizeRichText = (text: string | null | undefined): string | null | undefined => {
  if (text === null || text === undefined) return text
  // Remove spaces before newlines and spaces after newlines
  return text.replace(/ \n/g, "\n").replace(/\n /g, "\n")
}

export const useProgressNoteTreatmentPlanForm = (
  id: string,
  treatmentPlan: TreatmentPlan,
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData,
) => {
  const progressNoteForms = useProgressNoteFormContext((state) => state.progressNoteForms)

  const storeForm = progressNoteForms.get(id)?.treatmentPlanForm

  const storeFormValue = storeForm?.getValues()

  let defaultValues: Partial<TreatmentPlanFormType>

  if (storeFormValue) {
    defaultValues = { ...storeFormValue, formIsDirty: false }
  } else {
    const {
      title,
      recommended = " ",
      from = new Date().toISOString(),
      to,
      followUpInstructions = " ",
    } = treatmentPlan || {}

    defaultValues = {
      id,
      title,
      recommended: normalizeRichText(recommended),
      followUpInstructions: normalizeRichText(followUpInstructions),
      period: { from: new Date(from), to: to ? new Date(to) : undefined },
      formIsDirty: false,
      isTreatmentPlanOptional: isTreatmentPlanSectionOptional(episodeOfCareProgressNoteData),
    }
  }

  const storeFormState = useStoreFormState(storeForm)

  return {
    defaultValues,
    storeFormState,
  }
}
